import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { ArrowLeftOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, message, Space } from 'antd';

import { Editor, FooterActions, Loading, PageHeader } from '~/components';
import { normalizeDocument, preencherTemplate, setPageHeader } from '~/helpers/functions';
import { getStorage, setStorage } from '~/helpers/storage';

import NovoContext from '../NovoContext';
import { salvarContrato, editarContrato } from './actions';
import S from './styles';

function Finalizar() {
  const tinyRef = useRef();
  const history = useHistory();
  const { tipoRequest } = useContext(NovoContext);

  const [contrato, setContrato] = useState({});
  const [editar, setEditar] = useState(false);
  const [template, setTemplate] = useState('');
  const [loading, setLoading] = useState({ active: false, text: '' });

  const [desabilitaFinalizar, setDesabilitaFinalizar] = useState(false);

  const initialRender = useCallback(() => {
    if (!editar) {
      const temp = getStorage('contrato', 'session');
      setContrato(temp);

      setLoading({ active: true, text: 'Aguarde enquanto criamos a proposta' });
      preencherTemplate(temp.campos, temp.template).then((response) => {
        setStorage('novoTemplate', response, 'session');
        setLoading({ active: false, text: '' });
        return setTemplate(response);
      });
    }
  }, [editar]);

  useEffect(() => {
    initialRender();
  }, [initialRender]);

  const tratarCamposEnvio = async () => {
    const novoContrato = Promise.all(
      contrato.campos.map((item) => {
        const { valor, tipo } = item;

        switch (tipo) {
          case 'percentual':
          case 'moeda':
            return {
              ...item,
              valor: valor.replace(/[^a-z0-9,/]/g, ''),
            };
          case 'cpfCnpj':
            return {
              ...item,
              valor: normalizeDocument(valor),
            };
          default:
            return { ...item };
        }
      })
    );
    return novoContrato;
  };

  async function doSalvarContrato() {
    setLoading({ active: true, text: 'Aguarde enquanto salvamos a proposta' });
    setDesabilitaFinalizar(true);

    const campos = await tratarCamposEnvio();
    const empresaNome = campos.find((x) => x.nome === 'EmpresaNome' || x.nome === 'Empresa')?.valor;
    const data = { ...contrato, campos, empresa: empresaNome || contrato.empresa };
    delete data.camposRow;
    const request = tipoRequest === 'novo' ? salvarContrato : editarContrato;

    setLoading({ active: false, text: '' });

    await request(data).then(async (status) => {
      if (status === 200) {
        await setPageHeader(template).then((response) => {
          message.success(`Processo finalizado com sucesso`);
          setTemplate(response);

          setLoading({ active: false, text: '' });

          const redirectHome = () => {
            window.location = '/home';
          };

          const handleAfterPrint = () => {
            window.removeEventListener('afterprint', handleAfterPrint);
            redirectHome();
          };

          window.addEventListener('afterprint', handleAfterPrint);

          // Give TinyMCE a tick to apply the new content before printing.
          window.setTimeout(() => {
            tinyRef && tinyRef.current && tinyRef.current.imprimirTemplate();

            // Fallback redirect if afterprint doesn't fire (or user cancels print).
            window.setTimeout(() => {
              window.removeEventListener('afterprint', handleAfterPrint);
              redirectHome();
            }, 5000);
          }, 0);
        });
      } else {
        setDesabilitaFinalizar(false);
        message.error('Falha na tentativa de realizar a requisição.');
      }
    });
  }

  const editarTemplate = () => {
    setTemplate(contrato.template);
    setEditar((prev) => !prev);
  };

  return (
    <>
      <Loading active={loading.active} text={loading.text} />

      <PageHeader
        title={contrato.nome}
        extra={
          <Button danger onClick={() => history.push('/home')}>
            Cancelar
          </Button>
        }
      />

      <S.Content>
        <Editor
          className="margin-content"
          ref={tinyRef}
          content={template}
          disabled={!editar}
          onSave={setEditar}
          setLoading={setLoading}
        />

        {!editar ? (
          <FooterActions>
            <Button size="large" icon={<EditOutlined />} onClick={editarTemplate}>
              Editar Template
            </Button>

            <Space>
              <Button
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={() => history.push(`/${tipoRequest}/informar-dados`)}
              >
                Voltar
              </Button>

              <Button
                type="primary"
                size="large"
                disabled={loading.active || desabilitaFinalizar}
                icon={<SaveOutlined />}
                onClick={doSalvarContrato}
              >
                Finalizar
              </Button>
            </Space>
          </FooterActions>
        ) : null}
      </S.Content>
    </>
  );
}

export default Finalizar;
